import React, { useState } from 'react';
import { CircularProgress, IconButton, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckIcon from '@mui/icons-material/Check';
import type { RxRowAction, RxRowActionKey } from './rxRowActions';

export interface RxRowActionsMenuProps {
  rxNumber: string;
  /** From `buildRxRowActions` — order, labels and disabled reasons are decided there. */
  actions: RxRowAction[];
  onAction: (key: RxRowActionKey) => void;
  /** An action for this row is running; the button waits. */
  busy?: boolean;
}

/**
 * PRX-137 — the row's action menu on the Prescriptions tab.
 *
 * A disabled item says why under its label. MUI gives a disabled item no
 * hover, so a tooltip would never show. The button stops the click, because
 * the row it sits in opens the Rx detail.
 */
export const RxRowActionsMenu: React.FC<RxRowActionsMenuProps> = ({ rxNumber, actions, onAction, busy = false }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const main = actions.filter((a) => a.group === 'main');
  const forms = actions.filter((a) => a.group === 'forms');

  const renderItem = (action: RxRowAction) => {
    const isToggle = action.checked !== undefined;
    return (
      <MenuItem
        key={action.key}
        disabled={action.disabled}
        role={isToggle ? 'menuitemcheckbox' : 'menuitem'}
        aria-checked={isToggle ? action.checked : undefined}
        onClick={(e) => {
          e.stopPropagation();
          if (action.disabled) return;
          setAnchor(null);
          onAction(action.key);
        }}
        // MUI dims a disabled item to 38% opacity, which would also dim the
        // reason under it below readable contrast. The label is greyed through
        // `text.disabled` instead, and the reason stays readable at full opacity.
        sx={{ alignItems: 'flex-start', '&.Mui-disabled': { opacity: 1 } }}
      >
        {isToggle && (
          <ListItemIcon sx={{ mt: 0.5 }}>{action.checked ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
        )}
        <ListItemText
          inset={!isToggle && main.some((a) => a.checked !== undefined)}
          primary={action.label}
          secondary={action.disabled ? action.reason : undefined}
          primaryTypographyProps={{ color: action.disabled ? 'text.disabled' : 'text.primary' }}
          secondaryTypographyProps={{ sx: { whiteSpace: 'normal', maxWidth: 280 } }}
        />
      </MenuItem>
    );
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label={`Actions for Rx ${rxNumber}`}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        disabled={busy}
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
      >
        {busy ? <CircularProgress size={16} /> : <MoreVertIcon fontSize="small" />}
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        // Disabled items take keyboard focus (review L4), so a keyboard or
        // screen-reader user reaches the reason a disabled item gives.
        slotProps={{ list: { disabledItemsFocusable: true } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {main.map(renderItem)}
        {forms.length > 0 && <ListSubheader disableSticky>Forms</ListSubheader>}
        {forms.map(renderItem)}
      </Menu>
    </>
  );
};

export default RxRowActionsMenu;
